import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MixpanelService } from '@core/services';
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';
import { TranslateModule } from '@ngx-translate/core';
import { EllipsisTooltipComponent, InfoTooltipComponent } from '@shared/components';
import { AdditionalContactForm, InformedUser, RequesterUser } from '@shared/interfaces';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { FieldsetModule } from 'primeng/fieldset';
import { MultiSelect, MultiSelectModule } from 'primeng/multiselect';

@Component({
    selector: 'app-order-summary-additional-contact',
    standalone: true,
    imports: [
        TranslateModule,
        EllipsisTooltipComponent,
        ReactiveFormsModule,
        InfoTooltipComponent,
        FieldsetModule,
        CheckboxModule,
        FormsModule,
        DropdownModule,
        ButtonModule,
        MultiSelectModule,
        NgClass,
    ],
    templateUrl: './order-summary-additional-contact.component.html',
    styleUrls: ['./order-summary-additional-contact.component.scss'],
})
export class OrderSummaryAdditionalContactComponent implements OnChanges {
    @Input({ required: true }) accountOwnerEmail: string | null = null;
    @Input({ required: true }) additionalContactForm!: FormGroup<AdditionalContactForm>;
    @Input({ required: true }) requesters: RequesterUser[] = [];
    @Input({ required: true }) informedUsers: InformedUser[] = [];
    @Input({ required: true }) mixpanelArea: string = '';
    @Input({ required: true }) isAddNewInformedUserModalVisible: boolean = false;

    @Output() openAddNewInformedUserModal: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild(MultiSelect) informedUsersMultiselect!: MultiSelect;

    constructor(
        private mixpanelService: MixpanelService,
        private rolesAndPermissionsService: RolesAndPermissionsService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (this.requesters) {
            this.evaluateCheckboxState();
        }

        if (
            changes['isAddNewInformedUserModalVisible']?.currentValue === false &&
            changes['isAddNewInformedUserModalVisible']?.previousValue === true
        ) {
            this.informedUsersMultiselect.show(true);
        }
    }

    isTheAccountOwnerTheOnlyRequester(): boolean {
        return this.requesters.length === 1 && this.requesters[0].email === this.accountOwnerEmail;
    }

    selectAccountOwnerAsRequester() {
        if (this.additionalContactForm.controls.isRequesterAccountOwner.value === true) {
            const accountOwner = this.requesters.find(requester => requester.email === this.accountOwnerEmail);
            if (accountOwner) {
                this.additionalContactForm.controls.requester.setValue(accountOwner.id);
            }
            this.mixpanelService.logEvent(this.mixpanelArea + '-select-account-owner-as-requester');
        } else {
            this.additionalContactForm.controls.requester.reset();
        }
    }

    evaluateCheckboxState() {
        if (!this.rolesAndPermissionsService.showCurrentUserAsAccountOwner({ requesters: this.requesters })) {
            this.additionalContactForm.controls.isRequesterAccountOwner.disable();
            return;
        }

        if (this.isTheAccountOwnerTheOnlyRequester()) {
            this.additionalContactForm.controls.requester.setValue(this.requesters[0].id);
            this.mixpanelService.logEvent(this.mixpanelArea + '-requester-selected', {
                requester: this.requesters[0].email,
            });
            this.additionalContactForm.controls.isRequesterAccountOwner.setValue(true);
            return;
        }

        const requester = this.requesters.find(
            requester => requester.id === this.additionalContactForm.controls.requester.value
        );

        if (requester?.email === this.accountOwnerEmail) {
            this.additionalContactForm.controls.isRequesterAccountOwner.setValue(true);
        } else {
            this.additionalContactForm.controls.isRequesterAccountOwner.setValue(false);
        }

        if (requester) {
            this.mixpanelService.logEvent(this.mixpanelArea + '-requester-selected', {
                requester: requester?.email,
            });
        }
    }

    changeAccountOwnerRequesterSelection(): void {
        if (this.additionalContactForm.controls.isRequesterAccountOwner.disabled) return;

        this.additionalContactForm.controls['isRequesterAccountOwner'].setValue(
            !this.additionalContactForm.controls['isRequesterAccountOwner'].value
        );
        this.selectAccountOwnerAsRequester();
    }

    addNewInformedUser(): void {
        this.openAddNewInformedUserModal.emit(true);
        this.mixpanelService.logOpenModal(this.mixpanelArea + '-add-new-informed-user');
    }

    informedUsersSelect(event: any): void {
        this.mixpanelService.logEvent(this.mixpanelArea + '-informed-user-selected', {
            informed: this.additionalContactForm.controls.informedUsers.value,
        });

        if (event?.target?.tagName === 'svg' || event?.target?.tagName === 'path') {
            event.stopImmediatePropagation();
        }
    }
}
